import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  TextField,
  Typography
} from '@mui/material';

import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import LoadingButton from '../../../../../components/buttons/Button';

import {
  CreateComplianceVectorBIZcoreTD,
  UpdateComplianceVectorBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import global from "../../../../../global.scss";

const Data = {
  VectorCode: "",
  PillarNumber: "",
  Name: "",
  Description: "",
  Order: "",
  IsDeterministic: "",
  IsActive: "",
};

const pillarChoices = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
];

const sourceTypeChoices = [
  "MASTER_DATA",
  "COMMERCIAL_INVOICE",
  "BILL_OF_LADING",
  "FORM_7501",
  "PIVOT_TABLE",
  "FEDERAL_REGISTER",
  "VENDOR_AGREEMENT",
  "BUYING_AGENCY_AGREEMENT",
  "TRANSFER_PRICING_STUDY",
  "FACTORY_INVOICE",
];

const emptyDataSource = () => ({ source_order: "", document_type: "" });

const ComplienceVectorModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [dataSources, setDataSources] = useState([]);
  const [mode, setMode] = useState(1);

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setDataSources([]);
    setMode(1);
  };

  useEffect(() => {
    try {
      if (open) {
        if (update.defaultMode === 1) {
          // mode = 1 - create compliance vector
          setMode(1);
          setDataSources([]);
        } else if (update.defaultMode === 2) {
          // mode = 2 - edit compliance vector
          setMode(2);
          setFormData({
            ...formData,
            VectorCode: update.vector_code ?? "",
            PillarNumber: update.pillar_number ?? "",
            Name: update.name || "",
            Description: update.description || "",
            Order: update.order ?? "",
            IsDeterministic: update.is_deterministic !== undefined ? update.is_deterministic : "",
            IsActive: update.is_active !== undefined ? update.is_active : "",
          });
          setDataSources(
            (update.data_sources || []).map((ds) => ({
              source_order: ds.source_order,
              document_type: ds.document_type,
            }))
          );
        } else {
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDataSourceChange = (index, field, value) => {
    const updated = dataSources.map((ds, i) =>
      i === index ? { ...ds, [field]: value } : ds
    );
    setDataSources(updated);
  };

  const handleAddDataSource = () => {
    setDataSources([...dataSources, emptyDataSource()]);
  };

  const handleRemoveDataSource = (index) => {
    setDataSources(dataSources.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const obj = {
      vector_code: formData.VectorCode !== "" ? parseInt(formData.VectorCode, 10) : null,
      pillar_number: formData.PillarNumber,
      name: formData.Name || "",
      description: formData.Description || "",
      order: formData.Order,
      is_deterministic: formData.IsDeterministic,
      is_active: formData.IsActive,
      data_sources: dataSources.map((ds) => ({
        source_order: parseInt(ds.source_order, 10),
        document_type: ds.document_type,
      })),
    };

    if (mode === 1) {
      dispatch(CreateComplianceVectorBIZcoreTD(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else if (mode === 2) {
      dispatch(UpdateComplianceVectorBIZcoreTD(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else {
      toast.error("Invalid modal");
      handleClose();
    }
  };

  const isSubmitDisabled = !(
    formData.Name !== "" &&
    formData.PillarNumber !== "" &&
    formData.Order !== "" &&
    formData.IsDeterministic !== "" &&
    formData.IsActive !== "" &&
    dataSources.every((ds) => ds.source_order !== "" && ds.document_type !== "")
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"md"} className="modal" sx={{ width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
          <Box width={"100%"}
            sx={{ padding: "15px" }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>

            {/* Row 1: Name + Pillar + Vector Code */}
            <Box
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField
                label="Name"
                fullWidth
                name="Name"
                value={formData.Name}
                onChange={handleChange}
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                select
                label="Pillar number"
                fullWidth
                name="PillarNumber"
                value={formData.PillarNumber}
                onChange={handleChange}
              >
                {pillarChoices.map((item, index) => (
                  <MenuItem key={index} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Vector code"
                type="number"
                name="VectorCode"
                value={formData.VectorCode}
                onChange={handleChange}
                sx={{ width: "160px", flexShrink: 0 }}
                inputProps={{ min: 1 }}
                helperText="Stable ID — assign once"
              />
            </Box>

            {/* Row 2: Description */}
            <Box mb={"15px"}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                name="Description"
                value={formData.Description}
                onChange={handleChange}
              />
            </Box>

            {/* Row 3: Order + Is Deterministic + Is Active */}
            <Box
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField
                label="Order"
                fullWidth
                type="number"
                name="Order"
                value={formData.Order}
                onChange={handleChange}
              />
              <TextField
                select
                label="Is deterministic"
                fullWidth
                name="IsDeterministic"
                value={formData.IsDeterministic}
                onChange={handleChange}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </TextField>
              <TextField
                select
                label="Is active"
                fullWidth
                name="IsActive"
                value={formData.IsActive}
                onChange={handleChange}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </TextField>
            </Box>

            {/* Data Sources */}
            <Box mb={"5px"}>
              <Box className="flexCenterSBRow" mb={"10px"}>
                <Typography variant='body2' fontWeight={600}>
                  Data Sources
                </Typography>
                <IconButton size="small" onClick={handleAddDataSource}>
                  <AddCircleOutlineOutlinedIcon 
                  fontSize="small" 
                  color='primary'
                  />
                </IconButton>
              </Box>
              {dataSources.length === 0 && (
                <Typography variant='caption' color={"grey"}>
                  No data sources added yet.
                </Typography>
              )}
              {dataSources.map((ds, index) => (
                <Box
                  key={index}
                  className="flexCenter_Row"
                  gap={"10px"}
                  mb={"10px"}>
                  <TextField
                    label="Order"
                    type="number"
                    name="source_order"
                    value={ds.source_order}
                    onChange={(e) => handleDataSourceChange(index, "source_order", e.target.value)}
                    sx={{ width: "100px" }}
                    inputProps={{ min: 1 }}
                  />
                  <TextField
                    select
                    label="Document type"
                    fullWidth
                    value={ds.document_type}
                    onChange={(e) => handleDataSourceChange(index, "document_type", e.target.value)}
                  >
                    {sourceTypeChoices.map((choice) => (
                      <MenuItem key={choice} value={choice}>
                        {choice}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton size="small" onClick={() => handleRemoveDataSource(index)}>
                    <DeleteOutlineOutlinedIcon 
                      fontSize="small" 
                      color='error'
                      />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
          <Divider
            sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text'
              onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={isSubmitDisabled}
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ComplienceVectorModal;
